import { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "@/lib/cn";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

export type ScheduleGrid = boolean[][]; // [day_of_week][hour_of_day]

interface Props {
  value: ScheduleGrid;
  onChange: (next: ScheduleGrid) => void;
  /** When true (default) the grid lets users click & drag to paint. */
  editable?: boolean;
  /** Optional caption for assistive text. */
  ariaLabel?: string;
}

function cloneGrid(g: ScheduleGrid): ScheduleGrid {
  return g.map((row) => row.slice());
}

/** Make a fresh 7×24 false grid. */
export function makeEmptyScheduleGrid(): ScheduleGrid {
  return Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => false));
}

/** Make a fresh 7×24 grid with every hour active. */
export function makeFullScheduleGrid(): ScheduleGrid {
  return Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => true));
}

/**
 * 7×24 click-and-drag toggle grid. Reused by `idle_consumption` and
 * the v3 `active_when` editor. Drag direction is determined by the
 * cell the mouse pressed on (so a stroke either paints or erases —
 * not both, even if the cursor crosses both kinds).
 */
export function WeeklyScheduleGrid({
  value,
  onChange,
  editable = true,
  ariaLabel,
}: Props) {
  const dragging = useRef<{ paint: boolean } | null>(null);
  const [hover, setHover] = useState<{ day: number; hour: number } | null>(null);

  // Defensive copy when value mutates from outside.
  const grid = value;

  useEffect(() => {
    const onUp = () => {
      dragging.current = null;
    };
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, []);

  const apply = useCallback(
    (day: number, hour: number, paint: boolean) => {
      if (grid[day][hour] === paint) return;
      const next = cloneGrid(grid);
      next[day][hour] = paint;
      onChange(next);
    },
    [grid, onChange],
  );

  return (
    <div
      className="select-none border border-edge rounded overflow-hidden bg-surface-2/30"
      aria-label={ariaLabel ?? "Weekly schedule grid"}
    >
      <div className="grid" style={{ gridTemplateColumns: "auto repeat(24, minmax(0,1fr))" }}>
        <div className="bg-surface-2/40 border-b border-edge" />
        {Array.from({ length: 24 }).map((_, h) => (
          <div
            key={h}
            className="text-[9px] text-text-faint text-center py-1 border-b border-edge bg-surface-2/40"
          >
            {h}
          </div>
        ))}
        {DAYS.map((label, day) => (
          <>
            <div
              key={`label-${label}`}
              className="px-2 py-0.5 text-[10px] uppercase tracking-wider text-text-faint border-r border-edge bg-surface-2/30"
            >
              {label}
            </div>
            {Array.from({ length: 24 }).map((_, hour) => {
              const active = grid[day][hour];
              const isHover = hover?.day === day && hover?.hour === hour;
              return (
                <button
                  type="button"
                  key={`${day}-${hour}`}
                  disabled={!editable}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (!editable) return;
                    const paint = !active;
                    dragging.current = { paint };
                    apply(day, hour, paint);
                  }}
                  onMouseEnter={() => {
                    setHover({ day, hour });
                    if (!editable || !dragging.current) return;
                    apply(day, hour, dragging.current.paint);
                  }}
                  onMouseLeave={() => {
                    setHover(null);
                  }}
                  aria-pressed={active}
                  aria-label={`${label} ${hour.toString().padStart(2, "0")}:00 ${active ? "active" : "inactive"}`}
                  className={cn(
                    "h-5 border-r border-b border-edge/40 transition-colors",
                    active ? "bg-accent/60" : "bg-surface/40",
                    isHover && editable && "ring-1 ring-text-faint",
                    !editable && "cursor-not-allowed",
                  )}
                />
              );
            })}
          </>
        ))}
      </div>
    </div>
  );
}
